import { useEffect, useRef } from 'react';
import { Globe, Search, Settings, Headphones } from 'lucide-react';

const services = [
  {
    icon: Globe,
    title: 'Création de site web',
    description: 'Sites modernes, rapides et adaptés à votre entreprise.',
    features: ['Design sur mesure', 'Mobile responsive', 'Chargement rapide']
  },
  {
    icon: Search,
    title: 'Optimisation SEO',
    description: 'Amélioration de la visibilité sur Google.',
    features: ['Audit SEO', 'Mots-clés optimisés', 'Performance technique']
  },
  {
    icon: Settings,
    title: 'Outils digitaux',
    description: 'Automatisation, formulaires, systèmes de réservation.',
    features: ['Formulaires intelligents', 'Réservations en ligne', 'Automatisation']
  },
  {
    icon: Headphones,
    title: 'Maintenance et support',
    description: 'Accompagnement technique continu.',
    features: ['Support réactif', 'Mises à jour', 'Sauvegardes']
  }
];

export const ServicesSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = sectionRef.current?.querySelectorAll('.fade-in');
    cards?.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      id="services"
      ref={sectionRef}
      data-testid="services-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-medium text-[#1a365d] uppercase tracking-wider mb-4">
            Ce que nous faisons
          </span>
          <h2 
            data-testid="services-title"
            className="font-['Manrope'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-4"
          >
            Nos services
          </h2>
          <p className="text-base text-[#4A4A4A] max-w-2xl mx-auto">
            Des solutions digitales complètes pour propulser votre entreprise vers le succès.
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <div
              key={service.title}
              data-testid={`service-card-${index}`}
              className={`fade-in card-service p-8 rounded-2xl hover-lift stagger-${index + 1}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-start gap-6">
                <div className="service-icon flex-shrink-0 w-14 h-14 bg-[#1a365d]/10 rounded-xl flex items-center justify-center">
                  <service.icon className="text-[#1a365d]" size={28} />
                </div>
                <div className="flex-1">
                  <h3 className="font-['Manrope'] text-xl font-bold text-[#1A1A1A] mb-2">
                    {service.title}
                  </h3>
                  <p className="text-sm text-[#4A4A4A] mb-4 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                        <svg className="w-4 h-4 text-[#10B981] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
